import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table6 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[6] || 'Stock Balance');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectionStats, setSelectionStats] = useState(null);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[6] || 'Stock Balance';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Calculate average buying rate separately
  const calculateAverage = () => {
    try {
      const allRatesAndAmounts = [];
      
      // Load Stock Updates data
      const stockUpdatesData = localStorage.getItem('table_table3');
      let parsed = null;
      
      if (stockUpdatesData) {
        try {
          parsed = JSON.parse(stockUpdatesData);
        } catch (e) {
          console.error('Error parsing stock updates data:', e);
        }
      }

      // Process Stock Updates data if it exists
      if (parsed && parsed.length >= 2) {
        const headers = parsed[0];
        const buyingRateIndex = headers.indexOf('Buying Rate');
        const amountIndex = headers.indexOf('Amount');
        const currencyIndex = headers.indexOf('Currency');

        if (buyingRateIndex !== -1 && amountIndex !== -1 && currencyIndex !== -1) {
          parsed.slice(1).forEach(row => {
            if (!row || row.length === 0) return;

            const buyingRate = String(row[buyingRateIndex] || '').trim();
            const currency = String(row[currencyIndex] || '').trim();
            const amount = parseFloat(row[amountIndex]) || 0;

            if (buyingRate && currency && !isNaN(amount) && amount > 0) {
              const rateValue = parseFloat(buyingRate);
              allRatesAndAmounts.push({ rate: rateValue, amount: amount });
            }
          });
        }
      }
      
      // Calculate overall weighted average buying rate
      let overallAverage = 0;
      if (allRatesAndAmounts.length > 0) {
        let totalWeightedRate = 0;
        let totalAmount = 0;
        allRatesAndAmounts.forEach(({ rate, amount }) => {
          totalWeightedRate += rate * amount;
          totalAmount += amount;
        });
        overallAverage = totalAmount > 0 ? totalWeightedRate / totalAmount : 0;
      }
      
      return overallAverage;
    } catch (e) {
      console.error('Error calculating average:', e);
      return 0;
    }
  };

  // Calculate aggregated data from Stock Updates (table3) minus Transactions (table4)
  const calculateStockBalance = () => {
    try {
      // Initialize balance map - will be populated from Stock Updates and Transactions
      const balanceMap = {};
      const allRatesAndAmounts = []; // Store all rates and amounts for overall average
      let latestDate = '';
      
      // Load Stock Updates data
      const stockUpdatesData = localStorage.getItem('table_table3');
      let parsed = null;
      
      if (stockUpdatesData) {
        try {
          parsed = JSON.parse(stockUpdatesData);
        } catch (e) {
          console.error('Error parsing stock updates data:', e);
        }
      }

      // Process Stock Updates data if it exists
      if (parsed && parsed.length >= 2) {
        const headers = parsed[0];
        const buyingRateIndex = headers.indexOf('Buying Rate');
        const amountIndex = headers.indexOf('Amount');
        const currencyIndex = headers.indexOf('Currency');
        const dateIndex = headers.indexOf('Date');

        console.log(`📊 [STOCK BALANCE] Processing ${parsed.length - 1} stock update rows`);
        console.log(`📊 [STOCK BALANCE] Headers:`, headers);
        console.log(`📊 [STOCK BALANCE] Indices - Buying Rate: ${buyingRateIndex}, Amount: ${amountIndex}, Currency: ${currencyIndex}`);

        if (buyingRateIndex !== -1 && amountIndex !== -1 && currencyIndex !== -1) {
          let processedCount = 0;
          // Aggregate by Buying Rate and Currency from Stock Updates
          parsed.slice(1).forEach((row, rowIndex) => {
            if (!row || row.length === 0) return;

            const buyingRate = String(row[buyingRateIndex] || '').trim();
            const currency = String(row[currencyIndex] || '').trim();
            const amount = parseFloat(row[amountIndex]) || 0;
            const date = String(row[dateIndex] || '').trim();

            if (buyingRate && currency && !isNaN(amount) && amount > 0) {
              const key = `${buyingRate}_${currency}`;
              const rateValue = parseFloat(buyingRate);
              
              // Store for overall average calculation
              allRatesAndAmounts.push({ rate: rateValue, amount: amount });
              
              if (!balanceMap[key]) {
                balanceMap[key] = {
                  buyingRate: buyingRate,
                  currency: currency,
                  totalAmount: 0,
                  lastDate: date
                };
              }
              
              balanceMap[key].totalAmount += amount;
              processedCount++;
              
              console.log(`📊 [STOCK BALANCE] Row ${rowIndex + 1}: Added ${amount} ${currency} at rate ${buyingRate}. New total for ${key}: ${balanceMap[key].totalAmount}`);
              
              // Keep track of the latest date
              if (date && (!latestDate || date > latestDate)) {
                latestDate = date;
              }
              if (date && (!balanceMap[key].lastDate || date > balanceMap[key].lastDate)) {
                balanceMap[key].lastDate = date;
              }
            } else {
              console.log(`⚠️ [STOCK BALANCE] Row ${rowIndex + 1} skipped - buyingRate: "${buyingRate}", currency: "${currency}", amount: ${amount}`);
            }
          });
          console.log(`✅ [STOCK BALANCE] Processed ${processedCount} valid stock update rows`);
        } else {
          console.error(`❌ [STOCK BALANCE] Missing required columns - Buying Rate: ${buyingRateIndex}, Amount: ${amountIndex}, Currency: ${currencyIndex}`);
        }
      } else {
        console.log(`⚠️ [STOCK BALANCE] No stock updates data found or insufficient rows`);
      }
      
      // Calculate overall weighted average buying rate
      let overallAverage = 0;
      if (allRatesAndAmounts.length > 0) {
        let totalWeightedRate = 0;
        let totalAmount = 0;
        allRatesAndAmounts.forEach(({ rate, amount }) => {
          totalWeightedRate += rate * amount;
          totalAmount += amount;
        });
        overallAverage = totalAmount > 0 ? totalWeightedRate / totalAmount : 0;
      }

      // Load Transactions data and deduct used amounts
      const transactionsData = localStorage.getItem('table_table4');
      if (transactionsData) {
        try {
          const transactionsParsed = JSON.parse(transactionsData);
          if (transactionsParsed && transactionsParsed.length > 1) {
            const transHeaders = transactionsParsed[0];
            const transBuyingRateIndex = transHeaders.indexOf('Buying Rate');
            const transInrIndex = transHeaders.indexOf('INR');
            const transCurrencyIndex = transHeaders.indexOf('Currency');
            const transDateIndex = transHeaders.indexOf('Date');

            if (transBuyingRateIndex !== -1 && transInrIndex !== -1) {
              transactionsParsed.slice(1).forEach(row => {
                if (!row || row.length === 0) return;

                const buyingRate = String(row[transBuyingRateIndex] || '').trim();
                const inrAmount = parseFloat(row[transInrIndex]) || 0;
                const date = transDateIndex !== -1 ? String(row[transDateIndex] || '').trim() : '';

                if (buyingRate && !isNaN(inrAmount) && inrAmount > 0) {
                  // Transactions use INR, so match by Buying Rate and INR currency
                  const key = `${buyingRate}_INR`;
                  
                  // Deduct the INR amount used in transaction from stock balance
                  // Allow negative balances - don't use Math.max(0, ...)
                  if (balanceMap[key]) {
                    balanceMap[key].totalAmount = balanceMap[key].totalAmount - inrAmount;
                    
                    // Update last date if transaction date is more recent
                    if (date && (!balanceMap[key].lastDate || date > balanceMap[key].lastDate)) {
                      balanceMap[key].lastDate = date;
                    }
                  } else {
                    // Rate doesn't exist in stock balance, create new entry with negative amount
                    balanceMap[key] = {
                      buyingRate: buyingRate,
                      currency: 'INR',
                      totalAmount: -inrAmount, // Negative because it's only used in transactions
                      lastDate: date || ''
                    };
                  }
                }
              });
            }
          }
        } catch (e) {
          console.error('Error processing transactions data:', e);
        }
      }

      // Convert to array and sort by Buying Rate
      // Show all balances including negative ones (don't filter)
      const balanceData = Object.values(balanceMap)
        .filter(item => item.totalAmount !== 0) // Only filter out exactly zero balances
        .sort((a, b) => {
          const rateA = parseFloat(a.buyingRate) || 0;
          const rateB = parseFloat(b.buyingRate) || 0;
          return rateA - rateB;
        })
        .map((item, index) => {
          // Ensure totalAmount is a number and preserve negative values
          const totalAmount = typeof item.totalAmount === 'number' 
            ? item.totalAmount 
            : parseFloat(item.totalAmount) || 0;
          
          return [
            index + 1,
            item.buyingRate,
            totalAmount, // Preserve negative values when stock is used more than available
            item.currency,
            ''
          ];
        });

      const result = [
        ['S no', 'Buying Rate', 'Total Amount', 'Currency', 'Notes'],
        ...balanceData
      ];
      
      console.log(`✅ [STOCK BALANCE] Calculation complete: ${balanceData.length} balance entries`);
      balanceData.forEach((row, idx) => {
        console.log(`   ${idx + 1}. Rate: ${row[1]}, Amount: ${row[2]}, Currency: ${row[3]}`);
      });
      
      return result;
    } catch (e) {
      console.error('❌ [STOCK BALANCE] Error calculating stock balance:', e);
      return [['S no', 'Buying Rate', 'Total Amount', 'Currency', 'Notes']];
    }
  };

  // Load and sync Stock Updates (table3) and Transactions (table4) from MySQL
  useEffect(() => {
    const syncTableData = async () => {
      try {
        // Sync table3 (Stock Updates) from MySQL
        const table3Data = await tableAPI.getById('table3');
        if (table3Data && table3Data.length > 0) {
          localStorage.setItem('table_table3', JSON.stringify(table3Data));
        }
        
        // Sync table4 (Transactions) from MySQL
        const table4Data = await tableAPI.getById('table4');
        if (table4Data && table4Data.length > 0) {
          localStorage.setItem('table_table4', JSON.stringify(table4Data));
        }
        
        // Trigger recalculation
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        console.error('Error syncing table data:', error);
      }
    };
    
    // Sync immediately on mount
    syncTableData();
    
    // Sync every 2 seconds to catch updates
    const interval = setInterval(syncTableData, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Get current Stock Updates and Transactions data for memoization
  const stockUpdatesData = useMemo(() => {
    try {
      return localStorage.getItem('table_table3');
    } catch (e) {
      return null;
    }
  }, [refreshKey]);

  const transactionsData = useMemo(() => {
    try {
      return localStorage.getItem('table_table4');
    } catch (e) {
      return null;
    }
  }, [refreshKey]);

  // Calculate average buying rate
  const averageBuyingRate = useMemo(() => {
    return calculateAverage();
  }, [stockUpdatesData, refreshKey]);

  // Recalculate initialData whenever Stock Updates or Transactions data changes
  const initialData = useMemo(() => {
    console.log(`🔄 [TABLE6] Recalculating stock balance (refreshKey: ${refreshKey})`);
    // Force fresh read from localStorage to get latest data
    const freshStockUpdates = localStorage.getItem('table_table3');
    const freshTransactions = localStorage.getItem('table_table4');
    
    // If we have fresh data, use it for calculation
    if (freshStockUpdates || freshTransactions) {
      console.log(`📊 [TABLE6] Using fresh data from localStorage`);
    }
    
    const calculated = calculateStockBalance();
    console.log(`✅ [TABLE6] Calculated ${calculated.length - 1} stock balance rows`);
    
    // Also save the calculated data to localStorage so ExcelTable can load it
    if (calculated && calculated.length > 0) {
      localStorage.setItem('table_table6', JSON.stringify(calculated));
    }
    
    return calculated;
  }, [stockUpdatesData, transactionsData, refreshKey]);

  // Currency column is at index 3 (0-indexed)
  const currencyColumns = [3];
  // Total columns: Total Amount (2)
  const totalColumns = [2];

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Reload balance data when Stock Updates or Transactions change
  useEffect(() => {
    // Check periodically (every second) for updates from Stock Updates and Transactions tables
    const interval = setInterval(() => {
      const newData = calculateStockBalance();
      if (newData && newData.length > 0) {
        const currentData = localStorage.getItem('table_table6');
        const currentParsed = currentData ? JSON.parse(currentData) : [];
        const newDataStr = JSON.stringify(newData);
        
        // If data has changed, update localStorage and trigger re-render
        if (JSON.stringify(currentParsed) !== newDataStr) {
          localStorage.setItem('table_table6', newDataStr);
          setRefreshKey(prev => prev + 1); // Trigger re-render
        }
      }
    }, 1000);

    // Also listen for storage events (when table3 or table4 are updated in other tabs/components)
    const handleStorageChange = (e) => {
      if (e.key === 'table_table3' || e.key === 'table_table4' || e.key === null) {
        // Recalculate when table3 or table4 changes (null means any key changed)
        console.log(`🔄 [TABLE6] Storage change detected: ${e.key}`);
        setRefreshKey(prev => prev + 1);
      }
    };
    
    // Listen for custom events (when table3 or table4 are updated in same tab)
    const handleCustomStorageChange = () => {
      console.log(`🔄 [TABLE6] Custom storage change detected`);
      setRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('table3Updated', handleCustomStorageChange);
    window.addEventListener('table4Updated', handleCustomStorageChange);
    
    // Also listen for custom events (for same-tab updates)
    const handleTableUpdate = async (e) => {
      if (e.detail && (e.detail.tableId === 'table3' || e.detail.tableId === 'table4')) {
        console.log(`🔄 [TABLE6] Detected update to ${e.detail.tableId}, recalculating...`);
        
        // Immediately sync the updated data to localStorage
        if (e.detail.data && Array.isArray(e.detail.data)) {
          localStorage.setItem(`table_${e.detail.tableId}`, JSON.stringify(e.detail.data));
          console.log(`✅ [TABLE6] Synced ${e.detail.tableId} data to localStorage (${e.detail.data.length} rows)`);
        } else {
          // If data not in event, fetch from MySQL
          try {
            const freshData = await tableAPI.getById(e.detail.tableId);
            if (freshData && freshData.length > 0) {
              localStorage.setItem(`table_${e.detail.tableId}`, JSON.stringify(freshData));
              console.log(`✅ [TABLE6] Fetched and synced ${e.detail.tableId} from MySQL (${freshData.length} rows)`);
            }
          } catch (error) {
            console.error(`❌ [TABLE6] Error fetching ${e.detail.tableId}:`, error);
          }
        }
        
        // Force immediate recalculation by updating refreshKey
        // Use a small delay to ensure localStorage is updated
        setTimeout(() => {
        setRefreshKey(prev => prev + 1);
          console.log(`🔄 [TABLE6] Triggered recalculation after ${e.detail.tableId} update`);
        }, 100);
      }
    };
    
    window.addEventListener('tableDataUpdated', handleTableUpdate);
    
    // Also listen for table-specific events
    const handleTable3Update = (e) => {
      console.log(`🔄 [TABLE6] Table3 specific update detected`);
      if (e.detail && e.detail.data) {
        localStorage.setItem('table_table3', JSON.stringify(e.detail.data));
        setTimeout(() => setRefreshKey(prev => prev + 1), 100);
      }
    };
    
    const handleTable4Update = (e) => {
      console.log(`🔄 [TABLE6] Table4 specific update detected`);
      if (e.detail && e.detail.data) {
        localStorage.setItem('table_table4', JSON.stringify(e.detail.data));
        setTimeout(() => setRefreshKey(prev => prev + 1), 100);
      }
    };
    
    window.addEventListener('table3Updated', handleTable3Update);
    window.addEventListener('table4Updated', handleTable4Update);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('table3Updated', handleCustomStorageChange);
      window.removeEventListener('table4Updated', handleCustomStorageChange);
      window.removeEventListener('tableDataUpdated', handleTableUpdate);
      window.removeEventListener('table3Updated', handleTable3Update);
      window.removeEventListener('table4Updated', handleTable4Update);
    };
  }, []);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[6] = tableName; // Table6 has id 6
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  // Calculate total amount by currency
  const totalAmountsByCurrency = useMemo(() => {
    if (!initialData || initialData.length < 2) return { INR: 0, AED: 0 };
    const amountIndex = initialData[0].indexOf('Total Amount');
    const currencyIndex = initialData[0].indexOf('Currency');
    if (amountIndex === -1 || currencyIndex === -1) return { INR: 0, AED: 0 };
    
    const totals = { INR: 0, AED: 0 };
    initialData.slice(1).forEach(row => {
      const value = parseFloat(row[amountIndex]?.toString().replace(/[^0-9.-]/g, '') || '0') || 0;
      const currency = String(row[currencyIndex] || '').trim().toUpperCase();
      if (currency === 'INR' || currency === 'AED') {
        totals[currency] = (totals[currency] || 0) + value;
      }
    });
    return totals;
  }, [initialData]);
  
  // Calculate total amount (sum of all currencies)
  const totalAmount = useMemo(() => {
    return (totalAmountsByCurrency.INR || 0) + (totalAmountsByCurrency.AED || 0);
  }, [totalAmountsByCurrency]);

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginLeft: 'auto' }}>
              {averageBuyingRate > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Average Buying Rate:</span>
                  <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                    {averageBuyingRate.toFixed(2)}
                  </span>
                </div>
              )}
              {totalAmountsByCurrency.INR !== 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>INR:</span>
                <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                    ₹{totalAmountsByCurrency.INR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[2] && selectionStats[2].hasNumeric && selectionStats[2].byCurrency && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {selectionStats[2].byCurrency.INR && (
                        <>
                        <span>Selected INR: ₹{selectionStats[2].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[2].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </>
                      )}
                      {selectionStats[2].byCurrency.AED && (
                        <>
                        <span>Selected AED: AED {selectionStats[2].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[2].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              {totalAmountsByCurrency.AED !== 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>AED:</span>
                  <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                    AED {totalAmountsByCurrency.AED.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table6"
        tableName={tableName}
        onSelectionStatsChange={setSelectionStats}
      />
    </div>
  );
};

export default Table6;

